import {
  Stack,
  Text,
  Badge,
  Panel,
  PanelHeader,
  PanelTitle,
  PanelSection,
  PanelActions,
  InlineStack,
  SkeletonText,
  Icon,
  Tooltip,
  TooltipContent,
  Link,
} from '@customerio/pluma-components/react';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useAnalytics } from '@/services/analytics';
import { useWorkspaceHealthSimulation, useEnvHealthObjects } from '@/features/workspace-health';
import { useActiveHealthTab } from '../hooks';
import { formatDeltaPct } from './stat-card';

function formatCountWithDelta(current: number, prevDay: number): string {
  const delta = formatDeltaPct(current, prevDay);
  const formatted = current.toLocaleString();
  if (delta == null) return formatted;
  return `${formatted} (${delta})`;
}

export function ObjectsPanel() {
  const workspaceId = getWorkspaceIdFromCurrentUrl() ?? 0;
  const { track } = useAnalytics();
  const { simulate } = useWorkspaceHealthSimulation();

  const { activeTab } = useActiveHealthTab();
  const objectsQuery = useEnvHealthObjects(workspaceId, simulate, activeTab === 'data');
  const data = objectsQuery.data?.body;

  const totalProcessing = (data?.processed_today.current ?? 0) + (data?.related_updates.current ?? 0);
  const totalProcessingPrev = (data?.processed_today.prev_day ?? 0) + (data?.related_updates.prev_day ?? 0);

  return (
    <Panel>
      <PanelHeader>
        <PanelTitle>Custom objects</PanelTitle>
        <PanelActions>
          <Link
            href={`/workspaces/${workspaceId}/settings/objects`}
            variant="primary"
            onClick={() => void track('workspaceHealth', 'viewAllClicked', { Destination: 'objects' })}
          >
            View all
          </Link>
        </PanelActions>
      </PanelHeader>
      <PanelSection>
        {objectsQuery.isLoading ? (
          <SkeletonText size="s" lines={4} />
        ) : data != null && data.object_type_count > 0 ? (
          <Stack gap="100">
            <InlineStack alignInline="space-between" alignBlock="center" py="050">
              <Text size="s">Object types</Text>
              <Badge>{data.object_type_count.toLocaleString()}</Badge>
            </InlineStack>
            <InlineStack alignInline="space-between" alignBlock="center" py="050">
              <InlineStack gap="050" alignBlock="center">
                <Text size="s">Total objects</Text>
                <Tooltip isDisabled={data.types.length <= 1}>
                  <Icon name="help" size="xs" color="subtle" />
                  <TooltipContent>
                    <Stack gap="050" textAlign="left">
                      <Text size="xs" color="inherit">
                        Per-type breakdown:
                      </Text>
                      {data.types.map((t) => (
                        <Text key={t.id} size="xs" color="inherit">
                          {t.name}: {t.object_count.toLocaleString()}
                        </Text>
                      ))}
                    </Stack>
                  </TooltipContent>
                </Tooltip>
              </InlineStack>
              <Badge>{data.total_objects.toLocaleString()}</Badge>
            </InlineStack>
            <InlineStack alignInline="space-between" alignBlock="center" py="050">
              <InlineStack gap="050" alignBlock="center">
                <Text size="s">Total relationships</Text>
                <Tooltip isDisabled={data.types.length <= 1}>
                  <Icon name="help" size="xs" color="subtle" />
                  <TooltipContent>
                    <Stack gap="050" textAlign="left">
                      <Text size="xs" color="inherit">
                        Per-type breakdown:
                      </Text>
                      {data.types.map((t) => (
                        <Text key={t.id} size="xs" color="inherit">
                          {t.name}: {t.relationship_count.toLocaleString()}
                        </Text>
                      ))}
                    </Stack>
                  </TooltipContent>
                </Tooltip>
              </InlineStack>
              <Badge>{data.total_relationships.toLocaleString()}</Badge>
            </InlineStack>
            <InlineStack alignInline="space-between" alignBlock="center" py="050">
              <InlineStack gap="050" alignBlock="center">
                <Text size="s">Object-related processing today</Text>
                <Tooltip>
                  <Icon name="help" size="xs" color="subtle" />
                  <TooltipContent>
                    Processing generated by object and relationship changes, including related segment and campaign
                    updates.
                  </TooltipContent>
                </Tooltip>
              </InlineStack>
              <Badge>{formatCountWithDelta(totalProcessing, totalProcessingPrev)}</Badge>
            </InlineStack>
          </Stack>
        ) : data != null ? (
          <Text size="s" color="subtle">
            No custom objects configured.
          </Text>
        ) : null}
      </PanelSection>
    </Panel>
  );
}
